import 'regenerator-runtime/runtime';
import React from 'react';
import ReactDOM from 'react-dom';
import { compose } from 'redux';
import { FormattedMessage } from 'react-intl';

import AppStateHOC from '../lib/app-state-hoc.jsx';
import GUI from '../containers/gui.jsx';
import HashParserHOC from '../lib/hash-parser-hoc.jsx';
import log from '../lib/log.js';
import MessageBoxType from '../lib/message-box.js';
import Login from '../components/login/login.jsx';

const onClickLogo = () => {
    window.location = '#';
};

const onClickCheckUpdate = () => {
    log('User click check update');
};

const onClickCheckAgentUpdate = async () => {
    log('Checking agent update from playground web...');
    if (!window.store) return;
    const setUpdate = require('../reducers/update').setUpdate;
    const openUpdateModal = require('../reducers/modals').openUpdateModal;
    const { showAlertWithTimeout } = require('../reducers/alerts');

    // 1. Query local agent from port 20111
    let localAgent = null;
    try {
        const localRes = await fetch('http://127.0.0.1:20111/version');
        if (localRes.ok) {
            localAgent = await localRes.json();
        }
    } catch (_) {}

    if (!localAgent || !localAgent.version) {
        window.store.dispatch(setUpdate({
            phase: 'error',
            info: {
                message: 'TIF Studio Agent is not running. Please start the agent from the system tray and try again.'
            }
        }));
        window.store.dispatch(openUpdateModal());
        return;
    }

    // 2. Fetch latest version from VPS (with local fallback proxy to bypass browser CORS)
    try {
        let text = null;
        try {
            const res = await fetch('https://app.tifstudio.in/agent-updates/latest-agent.yml', {
                headers: { 'Cache-Control': 'no-cache' }
            });
            if (res.ok) {
                text = await res.text();
            }
        } catch (_) {
            // Browser CORS prevented direct fetch from localhost, try local dev proxy
            try {
                const proxyRes = await fetch('/api/check-agent-update');
                if (proxyRes.ok) {
                    text = await proxyRes.text();
                }
            } catch (e) {}
        }

        if (!text) {
            window.store.dispatch(setUpdate({
                phase: 'error',
                info: {
                    message: 'Failed to connect to update server. Please check your internet connection.'
                }
            }));
            window.store.dispatch(openUpdateModal());
            return;
        }

        const versionMatch = text.match(/version:\s*([^\r\n]+)/);
        if (!versionMatch) {
            window.store.dispatch(setUpdate({
                phase: 'error',
                info: {
                    message: 'Could not determine remote agent version.'
                }
            }));
            window.store.dispatch(openUpdateModal());
            return;
        }
        const remoteVersion = versionMatch[1].trim();

        const compareParts = (v1, v2) => {
            const p1 = (v1 || '').replace(/[^0-9.]/g, '').split('.').map(Number);
            const p2 = (v2 || '').replace(/[^0-9.]/g, '').split('.').map(Number);
            for (let i = 0; i < Math.max(p1.length, p2.length); i++) {
                const num1 = p1[i] || 0;
                const num2 = p2[i] || 0;
                if (num1 > num2) return 1;
                if (num1 < num2) return -1;
            }
            return 0;
        };

        if (compareParts(remoteVersion, localAgent.version) > 0) {
            window.store.dispatch(setUpdate({
                phase: 'resourceUpdateAvailable',
                info: {
                    version: remoteVersion,
                    message: {
                        en: {
                            'What is new': [`Latest version of agent is available to update (v${remoteVersion}).`]
                        }
                    }
                }
            }));
            window.store.dispatch(openUpdateModal());
        } else {
            window.store.dispatch(setUpdate({
                phase: 'latest',
                info: {
                    message: `TIF Studio Agent is already the latest version (v${localAgent.version}).`
                }
            }));
            window.store.dispatch(openUpdateModal());
        }
    } catch (err) {
        console.error('Failed to check agent update:', err);
        window.store.dispatch(setUpdate({
            phase: 'error',
            info: {
                message: 'Failed to check agent update. Please verify your internet connection.'
            }
        }));
        window.store.dispatch(openUpdateModal());
    }
};

const onClickUpdate = () => {
    log('User click update from playground web');
    window.open('https://tifstudio.in/agent.html', '_blank', 'noopener,noreferrer');
    if (window.store) {
        const closeUpdateModal = require('../reducers/modals').closeUpdateModal;
        const clearUpdate = require('../reducers/update').clearUpdate;
        window.store.dispatch(closeUpdateModal());
        window.store.dispatch(clearUpdate());
    }
};

const onAbortUpdate = () => {
    log('User click abort update');
};

const onClickClearCache = () => {
    log('User click clear cahce');
};

const onClickInstallDriver = () => {
    log('User click install driver');
};

const handleTelemetryModalCancel = () => {
    log('User canceled telemetry modal');
};

const handleTelemetryModalOptIn = () => {
    log('User opted into telemetry');
};

const handleTelemetryModalOptOut = () => {
    log('User opted out of telemetry');
};

const onClickAbout = [
    {
        title: (<FormattedMessage
            defaultMessage="About"
            description="Menu bar item for about"
            id="gui.desktopMenuBar.about"
        />),
        onClick: () => log('About')
    },
    {
        title: (<FormattedMessage
            defaultMessage="License"
            description="Menu bar item for license"
            id="gui.desktopMenuBar.license"
        />),
        onClick: () => log('License')
    },
    {
        title: (<FormattedMessage
            defaultMessage="Privacy policy"
            description="Menu bar item for privacy policy"
            id="gui.menuBar.privacyPolicy"
        />),
        onClick: () => log('Privacy Policy')
    },
    {
        title: (<FormattedMessage
            defaultMessage="Data settings"
            description="Menu bar item for data settings"
            id="gui.menuBar.dataSettings"
        />),
        onClick: () => log('Data Settings')
    },
];

const handleShowMessageBox = (type, message) => {
    if (type === MessageBoxType.confirm) {
        return confirm(message); // eslint-disable-line no-alert
    } else if (type === MessageBoxType.alert) {
        return alert(message); // eslint-disable-line no-alert
    }
};

// App component to manage authentication state
const App = ({ appTarget, WrappedGui, backpackHost, simulateScratchDesktop }) => {
    // Initialize authentication state by checking localStorage immediately
    const [isAuthenticated, setIsAuthenticated] = React.useState(() => {
        const userData = localStorage.getItem('user');
        if (userData) {
            try {
                JSON.parse(userData); // Validate it's valid JSON
                return true;
            } catch (e) {
                // Invalid data, clear it
                localStorage.removeItem('user');
                return false;
            }
        }
        return false;
    });
    const [showSignup, setShowSignup] = React.useState(false);

    // Listen for logout event from ProfileButton
    React.useEffect(() => {
        const handleLogoutEvent = () => {
            handleLogout();
        };
        window.addEventListener('userLogout', handleLogoutEvent);

        return () => {
            window.removeEventListener('userLogout', handleLogoutEvent);
        };
    }, []);

    const handleLoginSuccess = () => {
        setIsAuthenticated(true);
    };

    const handleLogout = () => {
        localStorage.removeItem('user');
        setIsAuthenticated(false);
    };

    const handleSignupSuccess = (userData) => {
        console.log('User signed up:', userData);
        // In a real app, you would save this data to a backend
        // For now, just switch to login page
        setShowSignup(false);
        // Optionally, you could auto-login the user after signup
        // setIsAuthenticated(true);
    };

    const handleSwitchToSignup = () => {
        setShowSignup(true);
    };

    const handleSwitchToLogin = () => {
        setShowSignup(false);
    };

    if (!isAuthenticated) {
        if (showSignup) {
            const Signup = require('../components/signup/signup.jsx').default;
            return <Signup onSignup={handleSignupSuccess} onSwitchToLogin={handleSwitchToLogin} />;
        }
        return <Login onLogin={handleLoginSuccess} onSwitchToSignup={handleSwitchToSignup} />;
    }

    // Use local asset host for both development and production builds
    // If opened as file://, use relative path to avoid CORS errors
    const assetHost = window.location.protocol === 'file:' ? '.' : window.location.origin;

    return (
        simulateScratchDesktop ?
            <WrappedGui
                canEditTitle
                isScratchDesktop
                assetHost={assetHost}
                onClickAbout={onClickAbout}
                showTelemetryModal
                canSave={false}
                onTelemetryModalCancel={handleTelemetryModalCancel}
                onTelemetryModalOptIn={handleTelemetryModalOptIn}
                onTelemetryModalOptOut={handleTelemetryModalOptOut}
                onAbortUpdate={onAbortUpdate}
                onClickCheckUpdate={onClickCheckUpdate}
                onClickCheckAgentUpdate={onClickCheckAgentUpdate}
                onClickUpdate={onClickUpdate}
                onClickClearCache={onClickClearCache}
                onClickInstallDriver={onClickInstallDriver}
                onShowMessageBox={handleShowMessageBox}
            /> :
            <WrappedGui
                canEditTitle
                backpackVisible
                showComingSoon
                assetHost={assetHost}
                backpackHost={backpackHost}
                canSave={false}
                onClickLogo={onClickLogo}
                onClickCheckUpdate={onClickCheckUpdate}
                onClickCheckAgentUpdate={onClickCheckAgentUpdate}
                onClickUpdate={onClickUpdate}
                onAbortUpdate={onAbortUpdate}
                onShowMessageBox={handleShowMessageBox}
            />
    );
};

/*
 * Render the GUI playground. This is a separate function because importing anything
 * that instantiates the VM causes unsupported browsers to crash
 * {object} appTarget - the DOM element to render to
 */
export default appTarget => {
    GUI.setAppElement(appTarget);

    // note that redux's 'compose' function is just being used as a general utility to make
    // the hierarchy of HOC constructor calls clearer here; it has nothing to do with redux's
    // ability to compose reducers.
    const WrappedGui = compose(
        AppStateHOC,
        HashParserHOC
    )(GUI);

    // TODO a hack for testing the backpack, allow backpack host to be set by url param
    const backpackHostMatches = window.location.href.match(/[?&]backpack_host=([^&]*)&?/);
    const backpackHost = backpackHostMatches ? backpackHostMatches[1] : null;

    const scratchDesktopMatches = window.location.href.match(/[?&]isScratchDesktop=([^&]+)/);
    let simulateScratchDesktop;
    if (scratchDesktopMatches) {
        try {
            // parse 'true' into `true`, 'false' into `false`, etc.
            simulateScratchDesktop = JSON.parse(scratchDesktopMatches[1]);
        } catch {
            // it's not JSON so just use the string
            // note that a typo like "falsy" will be treated as true
            simulateScratchDesktop = scratchDesktopMatches[1];
        }
    }

    if (process.env.NODE_ENV === 'production' && typeof window === 'object') {
        // Warn before navigating away
        window.onbeforeunload = () => true;
    }

    ReactDOM.render(
        <App
            appTarget={appTarget}
            WrappedGui={WrappedGui}
            backpackHost={backpackHost}
            simulateScratchDesktop={simulateScratchDesktop}
        />,
        appTarget);
};

